import { useNavigate } from '@solidjs/router';
import { createSignal, For, onCleanup, onMount, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import removeAmpersand from '../../tools/remove-ampersand';
import { SearchField } from './search-field';
import { createSearchClient, hitHref, hitOpensInNewTab, SearchHit } from './search-shared';

const MIN_QUERY_LENGTH = 1;
const SUGGESTION_LIMIT = 10;

type SearchAutocompleteProps = {
    value: string;
    onInput: (value: string) => void;
    /** Enter with nothing highlighted in the menu. */
    onSubmit: (value: string) => void;
    onClear?: () => void;
    placeholder?: string;
    ariaLabel?: string;
    autofocus?: boolean;
    inputRef?: (el: HTMLInputElement) => void;
    clearButtonRef?: (el: HTMLButtonElement) => void;
    class?: string;
    /** Distinct per instance on a page: it names the listbox the field controls. */
    id: string;
};

/* The search field with the DS dropdown menu of matching titles under it (Figma 1443:13694,
   menu 184:26836). Shared by the header band, the search page and the search widget so
   typing behaves the same everywhere. Picking a suggestion goes straight to that page;
   Enter with no pick hands the query to `onSubmit`. The menu floats over the content
   below rather than pushing it, like the DS popover. */
export const SearchAutocomplete = (props: SearchAutocompleteProps) => {
    const { localize, algoliaSettings, siteInfo } = useContext(AppContext);
    const navigate = useNavigate();
    /* Explicit rather than `createUniqueId`, so the listbox id reads the same in server
       markup, client markup and tests. */
    const listId = `${props.id}-suggestions`;

    const [suggestions, setSuggestions] = createSignal<SearchHit[]>([]);
    const [menuOpen, setMenuOpen] = createSignal(false);
    /* Keyboard highlight in the menu; -1 is "none", so Enter submits the query. */
    const [activeIndex, setActiveIndex] = createSignal(-1);

    let rootRef: HTMLDivElement | undefined;

    /* Suggestions are a nicety: without Algolia keys the field still submits. */
    const search = algoliaSettings.searchAppId && algoliaSettings.searchApiClientKey && siteInfo.siteId && siteInfo.target
        ? createSearchClient(
            { searchAppId: algoliaSettings.searchAppId, searchApiClientKey: algoliaSettings.searchApiClientKey },
            { siteId: siteInfo.siteId, target: siteInfo.target },
        )
        : undefined;

    let latestRequest = 0;

    const closeMenu = () => {
        setMenuOpen(false);
        setActiveIndex(-1);
    };

    const fetchSuggestions = async (text: string) => {
        const requestId = ++latestRequest;

        if (!search || text.length < MIN_QUERY_LENGTH) {
            setSuggestions([]);
            closeMenu();
            return;
        }

        try {
            const { hits } = await search.client.initIndex(search.indexName).search<SearchHit>(text, { hitsPerPage: SUGGESTION_LIMIT });

            if (requestId !== latestRequest) {
                return;
            }

            setSuggestions(hits);
            setActiveIndex(-1);
            setMenuOpen(hits.length > 0);
        } catch (error) {
            console.error('error searching', error);
        }
    };

    const handleInput = (value: string) => {
        props.onInput(value);
        fetchSuggestions(value.trim());
    };

    const handleClear = () => {
        closeMenu();
        props.onClear?.();
    };

    const goTo = (hit: SearchHit) => {
        closeMenu();

        if (hitOpensInNewTab(hit)) {
            window.open(hitHref(hit), '_blank', 'noopener');
            return;
        }

        navigate(hitHref(hit));
    };

    const submit = (value: string) => {
        const active = suggestions()[activeIndex()];

        if (menuOpen() && active) {
            goTo(active);
            return;
        }

        closeMenu();
        props.onSubmit(value);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!menuOpen()) {
            return;
        }

        const count = suggestions().length;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex((activeIndex() + 1) % count);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex((activeIndex() - 1 + count) % count);
                break;
            case 'Escape':
                /* First Escape closes the menu only; the header band listens for Escape on
                   the window to close itself, so keep this one from reaching it. */
                event.preventDefault();
                event.stopPropagation();
                closeMenu();
                break;
        }
    };

    onMount(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (rootRef && !rootRef.contains(event.target as Node)) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        onCleanup(() => document.removeEventListener('mousedown', handleMouseDown));
    });

    const optionId = (index: number) => `${listId}-option-${index}`;

    return (
        <div ref={rootRef} class={clsx('relative', props.class)}>
            <SearchField
                value={props.value}
                onInput={handleInput}
                onSubmit={submit}
                onClear={handleClear}
                onKeyDown={handleKeyDown}
                placeholder={props.placeholder}
                ariaLabel={props.ariaLabel}
                autofocus={props.autofocus}
                inputRef={props.inputRef}
                clearButtonRef={props.clearButtonRef}
                combobox={{
                    listId,
                    expanded: menuOpen(),
                    activeOptionId: activeIndex() >= 0 ? optionId(activeIndex()) : undefined,
                }}
            />

            <Show when={menuOpen()}>
                <div class="absolute inset-x-0 top-full z-10 pt-cvi-4">
                    <ul
                        id={listId}
                        role="listbox"
                        aria-label={localize('search-suggestions', 'Suggestions')}
                        class="m-0 max-h-cvi-search-suggestions list-none overflow-y-auto rounded-cvi-md border border-cvi-border-muted bg-cvi-bg-elevation-overlay px-px py-cvi-4 shadow-elevation-overlay"
                    >
                        <For each={suggestions()}>
                            {(hit, index) => (
                                <li
                                    id={optionId(index())}
                                    role="option"
                                    aria-selected={index() === activeIndex()}
                                    class={clsx(
                                        'cursor-pointer px-cvi-10 py-cvi-5 label-md font-cvi-regular text-cvi-fg-default transition-colors duration-200 hover:bg-cvi-bg-menu-hover',
                                        index() === activeIndex() && 'bg-cvi-bg-menu-active',
                                    )}
                                    onMouseMove={() => setActiveIndex(index())}
                                    /* mousedown, not click: the document listener above closes the
                                       menu on mousedown outside the root, and the field's blur
                                       must not beat the pick. */
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        goTo(hit);
                                    }}
                                >
                                    {removeAmpersand(hit.postTitle)}
                                </li>
                            )}
                        </For>
                    </ul>
                </div>
            </Show>
        </div>
    );
};
